import { useState } from "react";
import { Trash2, XCircle } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogTitle, AlertDialogTrigger,
} from "../ui/alert-dialog";
import { money } from "../../lib/api";

const PlanStatus = ({ o, busy, onPayment }) => {
  const [amt, setAmt] = useState("");
  const plan = (o.installments ?? 1) > 1;
  const paid = Number(o.amount_paid ?? 0);
  const left = Math.max(0, Number(o.total ?? 0) - paid);
  if (!plan && o.status !== "awaiting_payment") return null;
  return (
    <div className="mt-4 rounded-2xl border-2 border-[var(--ink)] bg-[var(--paper)] p-3" data-testid={`admin-plan-${o.reference}`}>
      <p className="font-bold">{plan ? `Payment plan · ${o.installments} weekly payments` : "Pay in full"}</p>
      <p className="font-mono-x">Paid {money(paid)} · Left {money(left)}</p>
      {plan && (
        <ul className="text-xs mt-1 text-[#3b3550]">
          {(o.schedule ?? []).map((s) => <li key={s.n}>#{s.n} {money(s.amount)} due {s.due}</li>)}
        </ul>
      )}
      {o.status === "awaiting_payment" && left > 0 && (
        <form className="flex gap-2 mt-2" onSubmit={(e) => { e.preventDefault(); onPayment(o, Number(amt)); setAmt(""); }}>
          <input type="number" step="0.01" min="0.01" max={left} required value={amt} onChange={(e) => setAmt(e.target.value)}
            placeholder="Amount received" data-testid={`admin-payment-input-${o.reference}`}
            className="flex-1 min-w-0 rounded-full border-2 border-[var(--ink)] bg-white px-3 py-1.5" />
          <button type="submit" disabled={busy} data-testid={`admin-payment-btn-${o.reference}`}
            className="rounded-full bg-[var(--ink)] text-[var(--paper)] px-4 py-1.5 font-semibold disabled:opacity-50">Record</button>
        </form>
      )}
    </div>
  );
};

export const ORDER_STATUSES = [
  ["awaiting_payment", "Awaiting payment", "var(--yellow)"],
  ["paid", "Paid - printing", "var(--lime)"],
  ["shipped", "Shipped", "var(--cyan)"],
  ["collected", "Collected", "var(--cyan)"],
  ["cancelled", "Cancelled", "#e5e1ee"],
];

const statusColor = (s) => ORDER_STATUSES.find((x) => x[0] === s)?.[2] ?? "#fff";

const isOverdue = (o) => {
  if (o.status !== "awaiting_payment") return false;
  const paid = Number(o.amount_paid ?? 0);
  const limit = Date.now() - 3 * 864e5;
  let running = 0;
  return (o.schedule ?? []).some((s) => {
    running += Number(s.amount ?? 0);
    return paid + 0.001 < running && new Date(`${s.due}T00:00:00`).getTime() <= limit;
  });
};

const DeleteButton = ({ o, busy, onDelete }) => (
  <AlertDialog>
    <AlertDialogTrigger asChild>
      <button disabled={busy} data-testid={`admin-delete-order-${o.reference}`} aria-label={`Delete order ${o.reference}`}
        className="rounded-full border-2 border-[var(--ink)] w-10 h-10 grid place-items-center bg-white hover:bg-[#ffd6e4] transition-colors">
        <Trash2 className="w-4 h-4" />
      </button>
    </AlertDialogTrigger>
    <AlertDialogContent data-testid="delete-order-dialog">
      <AlertDialogTitle>Delete order {o.reference}?</AlertDialogTitle>
      <AlertDialogDescription>
        This permanently removes {o.name}'s order from your list. No email is sent. Use this for test or duplicate orders.
      </AlertDialogDescription>
      <AlertDialogFooter>
        <AlertDialogCancel data-testid="delete-order-keep">Keep order</AlertDialogCancel>
        <AlertDialogAction data-testid="delete-order-confirm" onClick={() => onDelete(o)}>Delete</AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

const CancelButton = ({ o, busy, onCancel }) => (
  <AlertDialog>
    <AlertDialogTrigger asChild>
      <button disabled={busy} data-testid={`admin-cancel-${o.reference}`}
        className="rounded-full border-2 border-[var(--ink)] px-3 py-2 font-semibold text-sm bg-white hover:bg-[#ffd6e4] transition-colors flex items-center gap-1.5">
        <XCircle className="w-4 h-4" /> Cancel & email customer
      </button>
    </AlertDialogTrigger>
    <AlertDialogContent data-testid="cancel-order-dialog">
      <AlertDialogTitle>Cancel order {o.reference}?</AlertDialogTitle>
      <AlertDialogDescription>
        {o.name} will get an email saying the order was cancelled because payment wasn't received.
        {Number(o.amount_paid ?? 0) > 0 && ` They've paid ${money(o.amount_paid)} so far. Remember to refund that by bank transfer.`}
      </AlertDialogDescription>
      <AlertDialogFooter>
        <AlertDialogCancel data-testid="cancel-order-keep">Keep order</AlertDialogCancel>
        <AlertDialogAction data-testid="cancel-order-confirm" onClick={() => onCancel(o)}>Cancel order</AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

export const OrderCard = ({ o, onStatus, onPayment, onCancel, onDelete, busy, highlight }) => (
  <article id={`order-${o.reference}`} data-testid={`admin-order-${o.reference}`}
    className={`rounded-[24px] chunk p-5 sm:p-6 ${highlight ? "bg-[#fff3c4] ring-4 ring-[var(--pink)]" : "bg-white"}`}>
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <p className="font-mono-x font-bold text-lg flex items-center gap-2">{o.reference}
          {isOverdue(o) && <span data-testid={`admin-overdue-${o.reference}`} className="px-2 py-0.5 rounded-full bg-[#b00040] text-white text-xs font-sans">Overdue</span>}
        </p>
        <p className="text-sm text-[#5a5470]">{new Date(o.created_at).toLocaleString("en-NZ")}</p>
      </div>
      <div className="flex flex-wrap gap-2">
        {o.status === "awaiting_payment" && <CancelButton o={o} busy={busy} onCancel={onCancel} />}
        <DeleteButton o={o} busy={busy} onDelete={onDelete} />
        <select data-testid={`admin-order-status-${o.reference}`} value={o.status} disabled={busy}
          onChange={(e) => onStatus(o, e.target.value)}
          className="rounded-full border-2 border-[var(--ink)] px-3 py-2 font-semibold text-sm" style={{ background: statusColor(o.status) }}>
          {ORDER_STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </div>
    </div>
    <div className="grid sm:grid-cols-2 gap-4 mt-4 text-sm">
      <div>
        <p className="font-bold text-base">{o.name}</p>
        <a className="underline break-all" href={`mailto:${o.email}`}>{o.email}</a>
        {o.phone && <p>{o.phone}</p>}
        <p className="mt-2 font-semibold">{o.shipping_method === "pickup" ? "Pickup" : "Courier"}</p>
        {o.address && <p className="whitespace-pre-wrap">{o.address}</p>}
        {o.notes && <p className="mt-2 italic">"{o.notes}"</p>}
      </div>
      <div>
        <ul className="space-y-1">
          {(o.items || []).map((i, k) => (
            <li key={k} className="flex justify-between gap-2"><span>{i.qty} × {i.name}{i.color ? ` (${i.color})` : ""}</span><span className="font-mono-x">{money(i.price * i.qty)}</span></li>
          ))}
        </ul>
        {o.shipping_fee > 0 && <p className="flex justify-between mt-1"><span>Shipping</span><span className="font-mono-x">{money(o.shipping_fee)}</span></p>}
        {o.gift_wrap && <p data-testid={`admin-giftwrap-${o.reference}`} className="flex justify-between mt-1 font-semibold text-[#c8102e]"><span>Gift wrap</span><span className="font-mono-x">{money(o.gift_wrap_fee)}</span></p>}
        {o.gift_message && <p data-testid={`admin-giftmessage-${o.reference}`} className="mt-1 rounded-lg border-2 border-dashed border-[#c8102e] bg-[#fff5f7] px-2 py-1.5 text-sm"><strong>Card:</strong> <span className="font-serif-i text-base">"{o.gift_message}"</span></p>}
        <p className="flex justify-between mt-2 pt-2 border-t-2 border-[var(--ink)] font-bold"><span>Total</span><span className="font-mono-x">{money(o.total)}</span></p>
        <PlanStatus o={o} busy={busy} onPayment={onPayment} />
      </div>
    </div>
  </article>
);
